import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useBatteryStore } from '../../stores/batteryStore';
import { useConfigStore } from '../../stores/configStore';
import { changeNickname, updateUser, unblockUser } from '../../services/userService';
import { changePassword } from '../../lib/firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../lib/firebase/app';
import { deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import { deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../../lib/firebase/app';
import { colors } from '../../styles/colors';
import { maskEmail } from '../../utils/email';
import ImageCropModal from '../../components/ImageCropModal';
import { IconClose } from '../../components/icons';
import { Capacitor } from '@capacitor/core';

type ModalType = 'nickname' | 'password' | 'introduction' | 'blocked' | 'deleteAccount' | null;

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const cells = useBatteryStore((s) => s.cells);
  const maxCells = useBatteryStore((s) => s.maxCells);
  const config = useConfigStore((s) => s.config);
  const isKo = i18n.language === 'ko';

  const [modal, setModal] = useState<ModalType>(null);
  const [newNickname, setNewNickname] = useState('');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [introduction, setIntroduction] = useState(user?.introduction ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [adLoading, setAdLoading] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [blockedUsersInfo, setBlockedUsersInfo] = useState<Record<string, { nickname: string; email: string }>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isNative = Capacitor.isNativePlatform();
  const navigate = useNavigate();

  // Fetch blocked users info when blocked modal opens
  useEffect(() => {
    if (modal !== 'blocked' || !user?.blockedUserIds?.length) return;
    const fetchInfo = async () => {
      const info: Record<string, { nickname: string; email: string }> = {};
      await Promise.all(user.blockedUserIds.map(async (uid) => {
        if (blockedUsersInfo[uid]) { info[uid] = blockedUsersInfo[uid]; return; }
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) {
            const data = snap.data();
            info[uid] = { nickname: data.nickname ?? '?', email: data.email ?? '' };
          } else {
            info[uid] = { nickname: isKo ? '탈퇴한 사용자' : 'Deleted user', email: '' };
          }
        } catch {
          info[uid] = { nickname: uid.slice(0, 8), email: '' };
        }
      }));
      setBlockedUsersInfo(info);
    };
    fetchInfo();
  }, [modal, user?.blockedUserIds]);

  if (!user) return null;

  const pct = maxCells > 0 ? Math.floor((cells / maxCells) * 100) : 0;
  const isOvercharged = pct > 100;
  const barColor = isOvercharged ? '#00e5ff' : pct > 60 ? colors.batteryHigh : pct > 30 ? colors.batteryMedium : colors.batteryLow;

  const genderColor = user.gender === 'male' ? '#4a9eff' : user.gender === 'female' ? '#ff6b9d' : colors.textMuted;

  const handleChangeNickname = async () => {
    if (!newNickname.trim()) return;
    const cost = config.nicknameChangeCost;
    if (cells < cost) { setError(t('battery.low')); return; }
    setSaving(true); setError('');
    try {
      await changeNickname(user.uid, newNickname.trim());
      await useBatteryStore.getState().consume(user.uid, cost, 'NICKNAME_CHANGE');
      useAuthStore.getState().setUser({ ...user, nickname: newNickname.trim() });
      setModal(null); setNewNickname('');
    } catch (err: any) {
      setError(err.message === 'NICKNAME_TAKEN' ? t('auth.nicknameDuplicate') : (err.message ?? 'Error'));
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) return;
    if (newPwd.length < 6) { setError(isKo ? '비밀번호는 6자 이상이어야 합니다.' : 'Password must be at least 6 characters.'); return; }
    if (newPwd !== confirmPwd) { setError(isKo ? '새 비밀번호가 일치하지 않습니다.' : 'New passwords do not match.'); return; }
    setSaving(true); setError('');
    try {
      await changePassword(currentPwd, newPwd);
      setModal(null); setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      alert(isKo ? '비밀번호가 성공적으로 변경되었습니다.' : 'Password changed successfully.');
    } catch (err: any) {
      const code = err.code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError(isKo ? '현재 비밀번호가 틀립니다.' : 'Current password is incorrect.');
      } else if (code === 'auth/weak-password') {
        setError(isKo ? '비밀번호가 너무 약합니다.' : 'Password is too weak.');
      } else if (code === 'auth/too-many-requests') {
        setError(isKo ? '너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요.' : 'Too many attempts. Please try again later.');
      } else {
        setError(isKo ? '비밀번호 변경에 실패했습니다.' : 'Failed to change password.');
      }
    } finally { setSaving(false); }
  };

  const handleSaveIntroduction = async () => {
    setSaving(true);
    try {
      await updateUser(user.uid, { introduction: introduction.trim() } as any);
      useAuthStore.getState().setUser({ ...user, introduction: introduction.trim() });
      setModal(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShowAvatarMenu(false);
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result as string);
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleCropDone = async (blob: Blob) => {
    setCropImage(null);
    setUploadingImage(true);
    try {
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateUser(user.uid, { profileImageUrl: url } as any);
      useAuthStore.getState().setUser({ ...user, profileImageUrl: url });
    } catch (err) { console.error('[Profile] Upload failed:', err); }
    finally { setUploadingImage(false); }
  };

  const handleRemoveImage = async () => {
    setShowAvatarMenu(false);
    if (!confirm(isKo ? '프로필 사진을 삭제하시겠습니까?' : 'Remove profile image?')) return;
    try {
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await deleteObject(storageRef).catch(() => {});
      // For Google users, restore Google profile image
      const fbUser = auth.currentUser;
      const isGoogle = fbUser?.providerData?.some(p => p.providerId === 'google.com');
      const fallbackUrl = isGoogle ? (fbUser?.photoURL ?? '') : '';
      await updateUser(user.uid, { profileImageUrl: fallbackUrl } as any);
      useAuthStore.getState().setUser({ ...user, profileImageUrl: fallbackUrl || undefined });
    } catch (err) { console.error('[Profile] Remove image failed:', err); }
  };

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const cooldownDays = useConfigStore.getState().config.deletionCooldownDays ?? 7;

  /** Archive → delete users doc → delete Firebase Auth (in this order) */
  const performDeletion = async () => {
    const { archiveUserBeforeDeletion } = await import('../../services/accountService');
    await archiveUserBeforeDeletion(user.uid, 'user_requested');
    await deleteDoc(doc(db, 'users', user.uid));
    const fbUser = auth.currentUser;
    if (fbUser) {
      try {
        await deleteUser(fbUser);
      } catch {
        // Client-side delete failed — use Admin SDK via Cloud Function
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { app } = await import('../../lib/firebase/app');
          const fn = httpsCallable(getFunctions(app), 'deleteAuthAccount');
          await fn({ uid: user.uid });
        } catch (err) {
          console.error('[Profile] Auth deletion fallback failed:', err);
        }
      }
    }
  };

  const handleDeleteAccount = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    const isGoogle = fbUser.providerData?.some(p => p.providerId === 'google.com');
    const cooldownMsg = isKo
      ? `\n\n탈퇴 후 ${cooldownDays}일간 동일 계정으로 재가입할 수 없습니다.`
      : `\n\nYou cannot re-register with the same account for ${cooldownDays} days after deletion.`;
    if (isGoogle) {
      if (!confirm(t('profile.deleteAccountConfirm') + cooldownMsg)) return;
      if (!confirm(isKo ? '정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.' : 'This cannot be undone. Continue?')) return;
      try {
        // Re-authenticate Google user first (before any data deletion)
        const { GoogleAuthProvider, reauthenticateWithPopup } = await import('firebase/auth');
        if (Capacitor.isNativePlatform()) {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          const result = await FirebaseAuthentication.signInWithGoogle();
          const idToken = result.credential?.idToken;
          if (!idToken) throw new Error('Re-auth failed');
          const credential = GoogleAuthProvider.credential(idToken);
          await reauthenticateWithCredential(fbUser, credential);
        } else {
          const provider = new GoogleAuthProvider();
          await reauthenticateWithPopup(fbUser, provider);
        }
        // Re-auth succeeded — now safe to delete
        await performDeletion();
      } catch (err: any) {
        if (err.code === 'auth/popup-closed-by-user') return;
        if (err.code === 'auth/requires-recent-login') {
          alert(isKo ? '보안을 위해 다시 로그인 후 시도해주세요.' : 'Please re-login first.');
        } else {
          alert(isKo ? '탈퇴 처리에 실패했습니다. 다시 시도해주세요.' : 'Account deletion failed. Please try again.');
          console.error('[Profile] Delete failed:', err);
        }
      }
    } else {
      setDeletePassword('');
      setDeleteError('');
      setModal('deleteAccount');
    }
  };

  const executeDeleteAccount = async () => {
    if (!deletePassword.trim()) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const fbUser = auth.currentUser;
      if (!fbUser || !fbUser.email) return;
      // Re-authenticate first (before any data deletion)
      const credential = EmailAuthProvider.credential(fbUser.email, deletePassword);
      await reauthenticateWithCredential(fbUser, credential);
      // Re-auth succeeded — now safe to delete
      await performDeletion();
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setDeleteError(isKo ? '비밀번호가 올바르지 않습니다.' : 'Incorrect password.');
      } else {
        setDeleteError(err.message || 'Failed');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleUnblock = async (uid: string) => {
    await unblockUser(user.uid, uid);
    useAuthStore.getState().setUser({ ...user, blockedUserIds: user.blockedUserIds.filter((id) => id !== uid) });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto" style={{ backgroundColor: colors.background }}>
      <div className="p-3 pb-8">

        {/* === Profile Card === */}
        <div className="mb-3 rounded-xl p-4" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center gap-3">
            {/* Avatar with click menu */}
            <div className="relative">
              <button
                onClick={() => setShowAvatarMenu(!showAvatarMenu)}
                className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full"
                style={{ border: `2px solid ${genderColor}`, backgroundColor: colors.surface }}
              >
                {uploadingImage ? (
                  <span className="text-xs" style={{ color: colors.textMuted }}>...</span>
                ) : user.profileImageUrl ? (
                  <img src={user.profileImageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl">{user.gender === 'male' ? '👨' : user.gender === 'female' ? '👩' : '🧑'}</span>
                )}
              </button>
              {/* Edit pencil indicator */}
              <div className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full"
                style={{ backgroundColor: colors.primary, border: `2px solid ${colors.card}`, pointerEvents: 'none' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </div>
              {/* Avatar dropdown menu */}
              {showAvatarMenu && (
                <div className="absolute left-0 top-full z-30 mt-1 min-w-[140px] overflow-hidden rounded-lg shadow-xl"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <button onClick={() => { setShowAvatarMenu(false); fileInputRef.current?.click(); }}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-white/5" style={{ color: colors.text }}>
                    {isKo ? '앨범에서 선택' : 'Choose from Gallery'}
                  </button>
                  {isNative && (
                    <button onClick={() => { setShowAvatarMenu(false); cameraInputRef.current?.click(); }}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-white/5" style={{ color: colors.text }}>
                      {isKo ? '카메라로 촬영' : 'Take Photo'}
                    </button>
                  )}
                  {user.profileImageUrl && (
                    <button onClick={handleRemoveImage}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-white/5" style={{ color: colors.error }}>
                      {t('profile.removeImage')}
                    </button>
                  )}
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUploadImage} />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-base font-bold" style={{ color: colors.text }}>{user.nickname}</span>
                {user.isAdmin && (
                  <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ backgroundColor: colors.warning + '20', color: colors.warning }}>{isKo ? '관리자' : 'Admin'}</span>
                )}
              </div>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                {user.email}
                {auth.currentUser?.providerData?.some(p => p.providerId === 'google.com') && (
                  <svg width="14" height="14" viewBox="0 0 18 18" className="ml-1 inline-block align-text-bottom">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                )}
              </p>
              <p className="text-xs" style={{ color: genderColor }}>
                {isKo ? '성별' : 'Gender'}: {user.gender === 'male' ? (isKo ? '남성' : 'Male') : user.gender === 'female' ? (isKo ? '여성' : 'Female') : (isKo ? '비공개' : 'Unknown')}
              </p>
              {(user.introduction || (user as any).selfIntroduction) && (
                <p className="mt-1 text-[11px] line-clamp-2" style={{ color: colors.textMuted }}>{user.introduction || (user as any).selfIntroduction}</p>
              )}
            </div>

            {/* Battery display (right) */}
            <div className="flex items-end text-right">
              <span className="font-lcd text-lg font-bold leading-none" style={{ color: barColor, textShadow: isOvercharged ? `0 0 8px ${barColor}, 0 0 16px ${barColor}40` : 'none' }}>{pct}%</span>
            </div>
          </div>

          {/* Battery gauge bar + cell count */}
          <div className="relative mt-2">
            <div className="flex items-center justify-end text-[10px]" style={{ color: isOvercharged ? barColor : colors.textMuted }}>
              <span className="font-lcd font-bold">{cells.toLocaleString()} {t('battery.cells')}</span>
              {isOvercharged && <span className="ml-1.5 font-bold" style={{ color: barColor, textShadow: `0 0 4px ${barColor}` }}>OVERCHARGED</span>}
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: colors.surface }}>
              <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${Math.min(pct, 100)}%`, backgroundColor: barColor,
                boxShadow: isOvercharged ? `0 0 6px ${barColor}, 0 0 12px ${barColor}60` : 'none',
              }} />
            </div>
          </div>

          {/* Default + Auto charge info */}
          <div className="mt-2 flex items-center justify-between text-[10px]">
            <span style={{ color: colors.textMuted }}>{isKo ? '기본값' : 'Default'}: {config.signupDefaultCells.toLocaleString()}</span>
            {config.autoChargeIntervalMinutes > 0 && (config.autoChargeAmount ?? 0) > 0 && (
              <span className="flex items-center gap-1.5">
                <span style={{ color: colors.textMuted }}>
                  {isKo ? `자동 충전 +${(config.autoChargeAmount ?? 0).toLocaleString()}` : `Auto +${(config.autoChargeAmount ?? 0).toLocaleString()}`}
                </span>
                <AutoChargeTimer
                  lastChargeMs={
                    user.lastCellChargeAt?.toMillis?.() ??
                    ((user.lastCellChargeAt as any)?.seconds ? (user.lastCellChargeAt as any).seconds * 1000 : 0)
                  }
                  intervalMin={config.autoChargeIntervalMinutes}
                  isFull={cells >= maxCells}
                />
              </span>
            )}
          </div>
        </div>

        {/* === Actions Section === */}
        <p className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>
          {isKo ? '계정 관리' : 'Account'}
        </p>

        <div className="mb-3 overflow-hidden rounded-xl" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <ActionRow label={t('profile.changeNickname')}
            desc={t('profile.changeNicknameCost', { cost: config.nicknameChangeCost.toLocaleString() })}
            onClick={() => { setNewNickname(''); setError(''); setModal('nickname'); }} />
          <ActionRow label={t('profile.introduction')}
            desc={user.introduction || (isKo ? '자기소개를 작성하세요' : 'Write about yourself')}
            onClick={() => { setIntroduction(user.introduction ?? ''); setModal('introduction'); }} />
          <ActionRow label={t('profile.changePassword')}
            onClick={() => { setCurrentPwd(''); setNewPwd(''); setError(''); setModal('password'); }} />
          <ActionRow label={isKo ? '신고 내역' : 'Report History'}
            onClick={() => navigate('/reports')} />
          {user.isAdmin && (
            <ActionRow label={isKo ? '신고 접수 내역' : 'Received Reports'}
              onClick={() => navigate('/reports?view=received')}
              style={{ color: colors.warning }} />
          )}
          <ActionRow label={t('profile.blockedUsers')}
            desc={`${user.blockedUserIds?.length ?? 0}${isKo ? '명' : ' users'}`}
            onClick={() => setModal('blocked')} isLast />
        </div>

        {/* Mobile purchase & reward ad buttons */}
        {isNative ? (
          <div className="mt-3">
            <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.primary }}>
              {isKo ? '셀 충전' : 'Cell Packs'}
            </p>
            <div className="flex gap-2">
              {(config.cellPacks ?? []).map(pack => (
                <button key={pack.id}
                  className="flex-1 rounded-lg py-2.5 text-center text-xs font-bold text-white"
                  style={{ backgroundColor: colors.card, border: `1.5px solid ${colors.primary}`, color: colors.primary }}
                  onClick={async () => {
                    const { purchaseCellPack } = await import('../../services/iapService');
                    const result = await purchaseCellPack(pack.id);
                    if (result.success && result.cells > 0) {
                      await useBatteryStore.getState().charge(user.uid, result.cells, 'IAP_CHARGE');
                      alert(isKo ? `${result.cells.toLocaleString()}셀이 충전되었습니다!` : `${result.cells.toLocaleString()} cells charged!`);
                    } else if (result.error !== 'cancelled') {
                      alert(result.error ?? 'Purchase failed');
                    }
                  }}>
                  {isKo ? pack.label.ko : pack.label.en}
                </button>
              ))}
            </div>
            {/* Rewarded ad button */}
            {config.showRewardAdButton && config.rewardAdAmount > 0 && (
              <button
                disabled={adLoading}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-xs font-bold disabled:opacity-50"
                style={{ backgroundColor: '#FFB30018', color: '#FFB300', border: '1.5px solid #FFB30060' }}
                onClick={async () => {
                  const msg = isKo
                    ? `광고를 시청하면 ${config.rewardAdAmount.toLocaleString()}셀이 충전됩니다.\n시청하시겠습니까?`
                    : `Watch an ad to earn ${config.rewardAdAmount.toLocaleString()} cells.\nProceed?`;
                  if (!confirm(msg)) return;
                  setAdLoading(true);
                  try {
                    const { showRewardedAd } = await import('../../services/adService');
                    const rewarded = await showRewardedAd();
                    if (rewarded) {
                      await useBatteryStore.getState().charge(user.uid, config.rewardAdAmount, 'REWARD_AD');
                      alert(isKo ? `${config.rewardAdAmount.toLocaleString()}셀이 충전되었습니다!` : `${config.rewardAdAmount.toLocaleString()} cells charged!`);
                    } else {
                      alert(isKo ? '광고를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.' : 'Ad not available. Please try again later.');
                    }
                  } finally { setAdLoading(false); }
                }}>
                {adLoading
                  ? (isKo ? '광고 로딩 중...' : 'Loading ad...')
                  : <>🎬 {t('battery.rewardAd')} (+{config.rewardAdAmount.toLocaleString()} {t('battery.cells')})</>}
              </button>
            )}
          </div>
        ) : (
          <p className="mt-3 text-center text-xs" style={{ color: colors.textMuted }}>
            {isKo ? '셀 구매와 보상 광고 시청은 모바일 앱에서만 가능합니다.' : 'Cell purchase and reward ads are available on the mobile app only.'}
          </p>
        )}

        {/* Logout */}
        <button onClick={() => {
          if (confirm(isKo ? '로그아웃 하시겠습니까?' : 'Are you sure you want to logout?')) {
            useAuthStore.getState().logout();
          }
        }}
          className="mt-3 w-full rounded-xl py-3 text-center text-sm font-bold"
          style={{ backgroundColor: `${colors.error}15`, color: colors.error }}>
          {t('auth.logout')}
        </button>

        {/* === Danger Zone === */}
        <p className="mt-4 mb-1.5 px-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.error }}>
          {isKo ? '위험 영역' : 'Danger Zone'}
        </p>
        <div className="overflow-hidden rounded-xl" style={{ border: `1px dashed ${colors.error}40` }}>
          <button onClick={handleDeleteAccount}
            className="w-full px-4 py-3 text-left text-sm font-semibold transition-colors hover:bg-white/5"
            style={{ color: colors.error }}>
            {t('profile.deleteAccount')}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 pb-2 text-center">
          <p className="text-[10px]" style={{ color: colors.textMuted }}>
            &copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.
          </p>
        </div>
      </div>

      {/* === Modals === */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 w-full max-w-sm rounded-2xl"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            onClick={(e) => e.stopPropagation()}>

            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
              <h3 className="text-sm font-bold" style={{ color: colors.text }}>
                {modal === 'nickname' && t('profile.changeNickname')}
                {modal === 'password' && t('profile.changePassword')}
                {modal === 'introduction' && t('profile.introduction')}
                {modal === 'blocked' && t('profile.blockedUsers')}
                {modal === 'deleteAccount' && (isKo ? '회원 탈퇴' : 'Delete Account')}
              </h3>
              <button onClick={() => setModal(null)} className="rounded p-1 hover:bg-white/10">
                <IconClose size={16} color={colors.textMuted} />
              </button>
            </div>

            <div className="px-4 py-4">
              {modal === 'nickname' && (
                <>
                  <input value={newNickname} onChange={(e) => setNewNickname(e.target.value)}
                    placeholder={isKo ? '새 닉네임' : 'New nickname'}
                    className="mb-2 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }} />
                  <p className="mb-3 text-[11px]" style={{ color: colors.textMuted }}>
                    {t('profile.changeNicknameCost', { cost: config.nicknameChangeCost.toLocaleString() })}
                  </p>
                  {error && <p className="mb-2 text-xs" style={{ color: colors.error }}>{error}</p>}
                  <button onClick={handleChangeNickname} disabled={saving || !newNickname.trim()}
                    className="w-full rounded-lg py-2 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: colors.primary }}>
                    {saving ? '...' : t('common.confirm')}
                  </button>
                </>
              )}
              {modal === 'password' && (
                <>
                  <input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
                    placeholder={isKo ? '현재 비밀번호' : 'Current password'}
                    className="mb-2 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }} />
                  <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                    placeholder={isKo ? '새 비밀번호 (6자 이상)' : 'New password (6+)'}
                    className="mb-2 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }} />
                  <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                    placeholder={isKo ? '새 비밀번호 확인' : 'Confirm new password'}
                    className="mb-3 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }} />
                  {error && <p className="mb-2 text-xs" style={{ color: colors.error }}>{error}</p>}
                  <button onClick={handleChangePassword} disabled={saving || !currentPwd || !newPwd || !confirmPwd}
                    className="w-full rounded-lg py-2 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: colors.primary }}>
                    {saving ? '...' : t('common.confirm')}
                  </button>
                </>
              )}
              {modal === 'introduction' && (
                <>
                  <textarea value={introduction} onChange={(e) => setIntroduction(e.target.value)}
                    placeholder={isKo ? '자기소개를 입력하세요...' : 'Tell us about yourself...'}
                    rows={4}
                    className="mb-3 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }} />
                  <button onClick={handleSaveIntroduction} disabled={saving}
                    className="w-full rounded-lg py-2 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: colors.primary }}>
                    {saving ? '...' : t('common.save')}
                  </button>
                </>
              )}
              {modal === 'deleteAccount' && (
                <>
                  <p className="mb-3 text-xs" style={{ color: colors.textMuted }}>
                    {isKo ? '탈퇴하려면 현재 비밀번호를 입력해주세요. 이 작업은 되돌릴 수 없습니다.' : 'Enter your current password to delete your account. This cannot be undone.'}
                  </p>
                  <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder={isKo ? '비밀번호' : 'Password'} autoFocus
                    className="mb-3 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }}
                    onKeyDown={(e) => { if (e.key === 'Enter') executeDeleteAccount(); }} />
                  {deleteError && <p className="mb-2 text-xs" style={{ color: colors.error }}>{deleteError}</p>}
                  <button onClick={executeDeleteAccount} disabled={deleting || !deletePassword.trim()}
                    className="w-full rounded-lg py-2 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: colors.error }}>
                    {deleting ? '...' : (isKo ? '회원 탈퇴' : 'Delete Account')}
                  </button>
                </>
              )}
              {modal === 'blocked' && (
                (user.blockedUserIds ?? []).length === 0 ? (
                  <p className="py-4 text-center text-sm" style={{ color: colors.textMuted }}>
                    {isKo ? '차단한 사용자가 없습니다.' : 'No blocked users.'}
                  </p>
                ) : (
                  <div className="max-h-60 space-y-1.5 overflow-y-auto">
                    {user.blockedUserIds.map((blockedUid) => {
                      const info = blockedUsersInfo[blockedUid];
                      return (
                        <div key={blockedUid} className="flex items-center justify-between rounded-lg px-3 py-2.5"
                          style={{ backgroundColor: colors.card }}>
                          <div className="mr-3 min-w-0 flex-1">
                            <p className="truncate text-xs font-bold" style={{ color: colors.text }}>
                              {info?.nickname ?? '...'}
                            </p>
                            {info?.email && (
                              <p className="truncate text-[10px]" style={{ color: colors.textMuted }}>
                                {maskEmail(info.email, user.isAdmin)}
                              </p>
                            )}
                          </div>
                          <button onClick={() => {
                            setModal(null);
                            window.dispatchEvent(new CustomEvent('open-profile', {
                              detail: { uid: blockedUid, nickname: info?.nickname ?? '?' },
                            }));
                          }}
                            className="shrink-0 rounded px-2.5 py-1 text-[10px] font-bold"
                            style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}>
                            {isKo ? '프로필 보기' : 'View Profile'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image crop modal */}
      {cropImage && (
        <ImageCropModal
          src={cropImage}
          onDone={handleCropDone}
          onCancel={() => setCropImage(null)}
        />
      )}
    </div>
  );
}

/** Reusable settings-style row */
function ActionRow({ label, desc, onClick, isLast, style: extraStyle }: { label: string; desc?: string; onClick: () => void; isLast?: boolean; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/5"
      style={{ borderBottom: isLast ? undefined : `1px solid ${colors.border}` }}
    >
      <div>
        <p className="text-sm font-semibold" style={{ color: colors.text, ...extraStyle }}>{label}</p>
        {desc && <p className="mt-0.5 text-[11px] line-clamp-1" style={{ color: colors.textMuted }}>{desc}</p>}
      </div>
      <span className="text-xs" style={{ color: colors.textMuted }}>→</span>
    </button>
  );
}

function AutoChargeTimer({ lastChargeMs, intervalMin, isFull }: { lastChargeMs: number; intervalMin: number; isFull: boolean }) {
  const [remaining, setRemaining] = useState('');
  const [atZero, setAtZero] = useState(false);
  useEffect(() => {
    if (!intervalMin || intervalMin <= 0) { setRemaining('--'); return; }
    if (isFull) { setRemaining('✓'); setAtZero(false); return; }
    const update = () => {
      const nextMs = lastChargeMs + intervalMin * 60000;
      const diff = Math.max(0, nextMs - Date.now());
      if (diff <= 0) {
        setRemaining('⏳');
        setAtZero(true);
        return;
      }
      setAtZero(false);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(h > 0
        ? `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
        : `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [lastChargeMs, intervalMin, isFull]);
  return <span className="font-lcd text-[10px] font-bold" style={{ color: isFull ? colors.success : atZero ? colors.warning : colors.info }}>{remaining}</span>;
}
